/**
 * Cart Drawer Web Component
 */

// serialize function is now loaded globally from form-serialize.js

class CartDrawerComponent extends HTMLElement {
	constructor() {
		super();
		
		this.errorCodes = [
			400, 401, 402, 403.404, 405, 406, 409, 414, 415, 422, 423, 430, 500, 501,
			502, 503, 504, 530, 540, 783,
		];
		
		this.dialog = null;
		this.isInitialized = false;
		this.scrollPosition = 0;
		this.preOrderAgreementAccepted = false;
		this.preOrderDisclaimerCheckbox = null;
		this.preOrderDisclaimerListener = null;
	}

	connectedCallback() {
		if (this.isInitialized) return;
		this.isInitialized = true;
		
		this.dialog = new A11yDialog(this.querySelector(".cart__drawer-wrap"));

		this.dialog.on('show', () => {
			setTimeout(() => {
				document.body.classList.add("cart-drawer--open")
			}, 150);
		});
		
		// Listen to A11yDialog events to handle scroll position
		this.dialog.on('hide', () => {
			document.body.classList.remove("cart-drawer--open")
			// Restore scroll position when dialog is fully hidden
			setTimeout(() => {
				window.scrollTo(0, this.scrollPosition);
			}, 10);
		});
		
		// Add a safety mechanism to ensure scroll lock is cleaned up
		this.dialog.on('hide', () => {
			// Force unlock scroll lock after a delay to ensure it's cleaned up
			setTimeout(() => {
				if(this.scrollLock && this.scrollLock.hasAttribute('active')) {
					console.warn('Scroll lock was not properly cleaned up, forcing unlock');
					this.scrollLock.forceUnlock();
					this.scrollLock.removeAttribute('active');
				}
			}, 500);
		});
		
		// Initialize if URL hash is #cart
		if (window.location.hash === '#cart') {
			this.open();
		}
		
		// Initialize if URL parameter show-cart=true is present
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('show-cart') === 'true') {
			setTimeout(() => {
				this.open();
			}, 100);
			
			// Clean up URL parameter after opening
			const newUrl = new URL(window.location);
			newUrl.searchParams.delete('show-cart');
			window.history.replaceState({}, '', newUrl);
		}
		
		// Add data attribute event listeners
		this.setupDataAttributeListeners();
		
		// Add keyboard event listeners
		this.setupKeyboardListeners();
		
		// Set up product addons handling
		this.setupProductAddonsListener();
		
		// Initialize preorder disclaimer handling if present
		this.setupPreOrderDisclaimer();
	}

	get cartDrawerWrap() {
		return this.querySelector(".cart__drawer-wrap");
	}

	get cartDrawer() {
		return this.querySelector(".cart__drawer");
	}

	get cartItems() {
		return this.querySelector(".cart__drawer-items");
	}

	get bag() {
		return document.querySelector("[data-bag-count]");
	}

	get bagSr() {
		return document.querySelectorAll("[data-bag-count-sr]");
	}

	get checkoutButton() {
		return document.querySelector(".cart__drawer-footer-checkout");
	}

	get checkoutButtonTextEl() {
		return document.querySelector(".cart__drawer-footer-checkout span");
	}

	get scrollLock() {
		return document.querySelector('scroll-lock');
	}

	/**
	 * Dispatch custom cart events
	 * @param {string} eventName - Event name (cart-added, cart-updated, cart-error)
	 * @param {*} data - Data to pass with event
	 */
	dispatchCartEvent(eventName, data) {
		const event = new CustomEvent(eventName, { 
			detail: data,
			bubbles: true 
		});
		document.dispatchEvent(event);
	}

	/**
	 * Sets up event listeners for data attributes to control cart drawer
	 */
	setupDataAttributeListeners() {
		// Listen for clicks on elements with data-cart-open attribute
		document.addEventListener('click', (event) => {
			const target = event.target.closest('[data-cart-open]');
			if (target) {
				event.preventDefault();
				this.open();
			}
		});

		// Listen for clicks on elements with data-cart-close attribute
		document.addEventListener('click', (event) => {
			const target = event.target.closest('[data-cart-close]');
			if (target) {
				event.preventDefault();
				this.close();
			}
		});

	}

	/**
	 * Sets up keyboard event listeners for cart drawer
	 */
	setupKeyboardListeners() {
		// Listen for escape key to close cart
		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && this.isOpen()) {
				event.preventDefault();
				this.close();
			}
		});
	}

	/**
	 * Sets up product addons listener to handle adding addons when a product is added to cart
	 */
	setupProductAddonsListener() {
		// Track processed form IDs to prevent duplicate processing
		const processedForms = new Set();
		
		document.addEventListener('cart-added', (event) => {
			// Get the form ID from the event detail if available
			const formId = event.detail?.formId;
			
			if (!formId) {
				return;
			}
			
			// Skip if we've already processed this form for this cart addition
			if (processedForms.has(formId)) {
				return;
			}
			
			// Find the product-addons element that belongs to this form
			const productAddonsElement = document.querySelector(`product-addons[form="${formId}"]`);
			
			if (!productAddonsElement) {
				return;
			}
			
			// Get all checked addon inputs (checkboxes or radio buttons)
			const checkedAddons = productAddonsElement.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked');
			
			if (checkedAddons.length > 0) {
				// Mark this form as processed
				processedForms.add(formId);
				
				// Add all checked addons to cart
				this.addProductAddonsToCart(checkedAddons).finally(() => {
					// Remove from processed set after a delay to allow for new submissions
					setTimeout(() => {
						processedForms.delete(formId);
					}, 1000);
				});
			}
		});
	}

	/**
	 * Adds product addons to the cart
	 * @param {NodeList} checkedAddons - List of checked addon input elements
	 * @returns {Promise} Promise that resolves when all addons are added
	 */
	async addProductAddonsToCart(checkedAddons) {
		const addonPromises = [];
		
		// Add all addons in parallel for better performance
		for (const addonInput of checkedAddons) {
			const variantId = addonInput.getAttribute('data-variant-id') || addonInput.value;
			
			if (variantId) {
				const addonPromise = fetch(`${window.Shopify.routes.root}cart/add.js`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Requested-With': 'xmlhttprequest'
					},
					body: JSON.stringify({
						id: variantId,
						quantity: 1
					})
				}).then(response => response.json())
					.then(data => {
						if (data.status === undefined || !['422', '404'].includes(data.status.toString())) {
							console.log('Addon added to cart:', data);
							return data;
						} else {
							console.error('Error adding addon to cart:', data);
							throw new Error(data.description || 'Failed to add addon');
						}
					})
					.catch(error => {
						console.error('Error adding addon to cart:', error);
						return null;
					});
				
				addonPromises.push(addonPromise);
			}
		}
		
		// Wait for all addons to be added, then refresh cart drawer
		try {
			await Promise.all(addonPromises);
			// Refresh cart drawer with updated contents after all addons are added
			return await this.refreshCartWithSections();
		} catch (error) {
			console.error('Error adding addons:', error);
			// Still update bag counts even if some addons failed
			this.updateBagCounts();
			throw error;
		}
	}

	/**
	 * Check if the cart drawer is currently open
	 * @returns {boolean} True if cart is open
	 */
	isOpen() {
		return this.cartDrawerWrap && this.cartDrawerWrap.classList.contains("active");
	}

	updateCartContents(data) {
		if(data.sections && data.sections["cart-drawer-v2"]){
			const parser = new DOMParser();
			const cartHTML = parser.parseFromString(data.sections["cart-drawer-v2"], "text/html");					
			this.cartDrawer.innerHTML = cartHTML.querySelector(".cart__drawer").innerHTML;
		}
		if(data.item_count !== undefined){
			if(data.item_count > 0){
				if(document.querySelector("#CartCount") !== null){
					document.querySelector("#CartCount").style.display = "flex";
				}
			} else {
				if(document.querySelector("#CartCount") !== null){
					document.querySelector("#CartCount").style.display = "none";
				}
			}
		} else {
			if(data.quantity > 0){
				if(document.querySelector("#CartCount") !== null){
					document.querySelector("#CartCount").style.display = "flex";
				}
			} else {
				if(document.querySelector("#CartCount") !== null){
					document.querySelector("#CartCount").style.display = "none";
				}
			}
		}
		
		// Ensure preorder disclaimer logic is (re)bound after cart HTML updates
		this.setupPreOrderDisclaimer();
	}

	/**
	 * Sets up handling for preorder disclaimer checkbox controlling checkout button
	 */
	setupPreOrderDisclaimer() {
		const checkoutButton = this.checkoutButton;
		if (!checkoutButton) return;

		// Clean up old listener if we had one
		if (this.preOrderDisclaimerCheckbox && this.preOrderDisclaimerListener) {
			this.preOrderDisclaimerCheckbox.removeEventListener(
				'change',
				this.preOrderDisclaimerListener
			);
		}

		const disclaimer = this.cartDrawer
			? this.cartDrawer.querySelector('.cart__drawer-footer-pre-order-items-disclaimer')
			: null;
		const checkbox = disclaimer
			? disclaimer.querySelector('input[type="checkbox"]')
			: null;

		// If no disclaimer/checkbox, ensure checkout is enabled and reset internal state
		if (!disclaimer || !checkbox) {
			checkoutButton.classList.remove('disabled');
			this.preOrderDisclaimerCheckbox = null;
			this.preOrderDisclaimerListener = null;
			this.preOrderAgreementAccepted = false;
			return;
		}

		// Restore previous agreement state onto the new checkbox instance
		checkbox.checked = !!this.preOrderAgreementAccepted;

		const updateCheckoutState = () => {
			if (checkbox.checked) {
				checkoutButton.classList.remove('disabled');
			} else {
				checkoutButton.classList.add('disabled');
			}
		};

		// Listener keeps internal state in sync
		this.preOrderDisclaimerListener = (event) => {
			this.preOrderAgreementAccepted = !!event.target.checked;
			updateCheckoutState();
		};

		// Set initial state based on restored checkbox value
		updateCheckoutState();

		// Bind listener to the current checkbox instance and track it
		checkbox.addEventListener('change', this.preOrderDisclaimerListener);
		this.preOrderDisclaimerCheckbox = checkbox;
	}
	
    /**
     * Refreshes the slide out cart drawer's content using fetch()
     * @returns {(Promise|Object)} Returns the cart's json and then returns the cart object
     */
	updateBagCounts() {
		fetch(`${window.Shopify.routes.root}cart.js`, {
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest'
			}
		}).then(response => {
			return response.json();
		}).then(cart => {
			console.log('Cart data:', cart);
			
			// Update all cart count elements
			const bagElements = document.querySelectorAll('[data-bag-count]');
			console.log('Found bag elements:', bagElements.length);
			
			bagElements.forEach(element => {
				element.textContent = cart.item_count;
			});
			
			// Update cart count visibility
			const cartCountElement = document.querySelector("#CartCount");
			if (cartCountElement) {
				if (cart.item_count > 0) {
					cartCountElement.style.display = "flex";
				} else {
					cartCountElement.style.display = "none";
				}
			}
			
			// Dispatch extra custom event to signal that the cart has been updated 
			// window.addEventListener('cartUpdated', (event) => { ... }
			const cartUpdatedEvent = new CustomEvent('cartUpdated', { detail: cart });
			window.dispatchEvent(cartUpdatedEvent);			
		})
	}
    /**
     * OPEN CART // Opens the slide out cart by adding the class "active" to the $cartDrawerWrap element
     */ 
	open() {
		// Save current scroll position before opening
		this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

		// Set scroll lock BEFORE showing dialog to prevent any scroll issues
		if(this.scrollLock){
			this.scrollLock.setAttribute('active', '');
		}
		
		this.dialog.show();
		this.cartDrawerWrap.classList.add("active");
	}
    /**
     * CLOSE CART // Closes the slide out cart by removing the class "active" to the $cartDrawerWrap element
     */ 
	close() {
		this.dialog.hide();
		if (this.cartDrawerWrap.classList.contains("active")) {
			this.cartDrawerWrap.classList.remove("active")
		}
		
		// Remove scroll lock AFTER dialog is hidden to ensure proper cleanup
		if(this.scrollLock){
			// Use setTimeout to ensure the dialog hide animation completes
			setTimeout(() => {
				this.scrollLock.removeAttribute('active');
			}, 100);
		}
	}

	/**
     * REFRESH THE CART // Refreshes the cart by fetching the cart.js file
     * @param  {String} form_id  The ID of the form to submit
     * @returns {(Promise|Function)} Returns the cart's json and then returns runs updateBagCounts() and showCart()
     * @example window.refreshCart(); return false;
     */ 
	refreshCart() {
		fetch(`${window.Shopify.routes.root}cart.js`, {
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest'
			}
		}).then(response => {
			return response.json();
		}).then(cart => {
			this.updateCartContents(cart);
		});
	}

	/**
	 * REFRESH CART WITH SECTIONS // Refreshes the cart drawer HTML and cart data
	 * @returns {Promise} Updates the cart drawer contents with latest cart HTML and data
	 * @example cartDrawer.refreshCartWithSections();
	 */
	refreshCartWithSections() {
		return Promise.all([
			fetch(`${window.Shopify.routes.root}?sections=cart-drawer-v2`, {
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			}).then(response => response.json()),
			fetch(`${window.Shopify.routes.root}cart.js`, {
				headers: {
					'Content-Type': 'application/json',
					'X-Requested-With': 'xmlhttprequest'
				}
			}).then(response => response.json())
		]).then(([sections, cart]) => {
			this.updateCartContents({
				sections: sections,
				item_count: cart.item_count
			});
			this.updateBagCounts();
			console.log('cart', cart)
			this.dispatchCartEvent('cart-updated', cart);
			return cart;
		}).catch(error => {
			console.error('Error refreshing cart with sections:', error);
			// Fallback: just update bag counts
			this.updateBagCounts();
			throw error;
		});
	}

	/**
	 * GET LINE ITEM PROPERTIES // Extracts line item properties from product__line-item-properties section
	 * @param  {String} form_id  The ID of the form
	 * @param  {Object} existingProperties  Existing properties object to merge into
	 * @returns {Object} Properties object with line item properties added
	 */
	getLineItemProperties(form_id, existingProperties = {}) {
		const properties = { ...existingProperties };
		const lineItemPropertiesContainer = document.querySelector(`.product__line-item-properties[form="${form_id}"]`);
		
		if (lineItemPropertiesContainer) {
			const lineItemPropertyInputs = lineItemPropertiesContainer.querySelectorAll('input[type="hidden"][name^="properties["]');
			if (lineItemPropertyInputs.length > 0) {
				lineItemPropertyInputs.forEach(input => {
					const propName = input.name.match(/properties\[(.*?)\]/)?.[1];
					if (propName && input.value) {
						properties[propName] = input.value;
					}
				});
			}
		}
		
		return properties;
	}

    /**
     * ADD ITEM TO THE CART // Submits a product form via fetch() and the product to the slide out cart
     * @param  {String} form_id  The ID of the form to submit
     * @returns {(Promise|Function)} Returns the cart's json and then returns runs updateBagCounts() and showCart()
     * @example window.addToCart('add-to-cart'); return false;
     */ 
	addToCart(form_id) { 
		// Check if personalization is active AND the form matches the personalization instance
		const personalization = window.productPersonalizationInstance;
		if (personalization && personalization.isPersonalizationActive() && personalization.matchesFormId(form_id)) {
			return this.addToCartWithPersonalization(form_id);
		}

		if (this.checkoutButton) {
			this.checkoutButton.setAttribute("disabled", true)
			this.checkoutButtonTextEl.textContent = window.checkoutUpdatingButtonText;
		}
		if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
			document.getElementById(form_id).querySelector("[data-button-atc-text]").disabled = true;
			document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartLoadingButtonText;
		}
		const formElement = document.getElementById(form_id);
		const formData = serialize(formElement, { hash: true });

		// Ensure preorder line item property is always correctly set
		const preOrderInput = formElement
			? formElement.querySelector('input[name="properties[Pre Order]"]')
			: null;

		// Normalize properties object
		if (!formData.properties || typeof formData.properties !== 'object') {
			formData.properties = {};
		}

		// Handle cases where serialize may have produced a flat key like "properties[Pre Order]"
		if (formData['properties[Pre Order]'] && !formData.properties['Pre Order']) {
			formData.properties['Pre Order'] = formData['properties[Pre Order]'];
			delete formData['properties[Pre Order]'];
		}

		// Prefer the DOM value so it's always in sync with the latest variant change
		if (preOrderInput && preOrderInput.value) {
			formData.properties['Pre Order'] = preOrderInput.value;
		} else if (formData.properties['Pre Order'] === '') {
			// Clean up empty preorder properties
			delete formData.properties['Pre Order'];
		}

		// Add personalization inputs that have the form attribute set
		const personalizationInputs = document.querySelectorAll(`product-personalization input[form="${form_id}"]`);

    if(personalizationInputs.length > 0){
      formData.properties = formData.properties || {};
      personalizationInputs.forEach(input => {
        if (input.type === 'radio' || input.type === 'checkbox') {
          if (input.checked) {
            formData.properties = formData.properties || {};
            const propName = input.name.match(/properties\[(.*?)\]/)?.[1];
            if (propName) {
              formData.properties[propName] = input.value;
            }
          }
        } else if (input.type === 'text' && input.value) {
          formData.properties = formData.properties || {};
          const propName = input.name.match(/properties\[(.*?)\]/)?.[1];
          if (propName) {
            formData.properties[propName] = input.value;
          }
        }
      });
    }

		// Add line item properties from product__line-item-properties section
		formData.properties = this.getLineItemProperties(form_id, formData.properties || {});
		console.log(formData);

		fetch(`${window.Shopify.routes.root}cart/add.js`, {
			body: JSON.stringify({
				...formData,
				sections: "cart-drawer-v2"
			}),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest'
			},
			method: 'POST'
		}).then(response => {
			return response.json();
		}).then((data) => {

			this.updateCartContents(data);

			if (data.status !== undefined && this.errorCodes.includes(data.status)) {
				alert(data.description);
				this.dispatchCartEvent('cart-error', data);
			} else {
				// KLAVIYO PRODUCT TRACKING
				var _learnq = window._learnq || [];
				var kl = {
					Name: `${data.product_title}`,
					ProductID: `${data.id}`,
					ImageURL: `${data.image}`,
					URL: `${window.location.origin}${data.url}`,
					Brand: `${data.vendor}`,
					Price: `${Shopify.formatMoney(data.final_price)}`,
					CompareAtPrice: `${Shopify.formatMoney(data.original_price)}`,
					VariantTitle: `${data.variant_title}`,
					VariantPrice: `${Shopify.formatMoney(data.final_price)}`,
					VariantImage: `${data.image}`,
					VariantSKU: `${data.sku}`,
					VariantID: `${data.variant_id}`,
					VariantOption1: `${data.variant_options[0] ? data.variant_options[0] : ""}`,
					VariantOption2: `${data.variant_options[1] ? data.variant_options[1] : ""}`,
					VariantOption3: `${data.variant_options[2] ? data.variant_options[2] : ""}`
				};
				_learnq.push(['track', 'Added to Cart', kl]);		
				console.log(`KL Added to Cart`, kl)			
				this.updateBagCounts()
        this.refreshCart();
				
				// Dispatch cart-added event with form ID for addon processing
				this.dispatchCartEvent('cart-added', { ...data, formId: form_id });
				
				if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[name='add']") !== null){
					document.getElementById(form_id).querySelector("[name='add']").disabled = false;
					document.getElementById(form_id).querySelector("[name='add']").classList.remove("disabled");
					document.getElementById(form_id).querySelector("[name='add']").classList.remove("loading");
				}

				if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
					document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartAddedButtonText;
				}
				setTimeout(() => {		
					if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
						document.getElementById(form_id).querySelector("[data-button-atc-text]").disabled = false;
					}
					setTimeout(() => {
						this.open()
					}, 150);
					setTimeout(() => {				
						if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
							document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartButtonText;
						}
					}, 500);
				}, 300);
			}
		})
	}

	/**
	 * GENERATE REFERENCE KEY // Creates a unique 10-character reference key
	 * @returns {String} A unique 10-character alphanumeric string
	 */
	generateReferenceKey() {
		const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
		let result = '';
		for (let i = 0; i < 10; i++) {
			result += chars.charAt(Math.floor(Math.random() * chars.length));
		}
		return result;
	}

	/**
	 * ADD TO CART WITH PERSONALIZATION // Handles adding product with personalization fee
	 * @param  {String} form_id  The ID of the main product form
	 * @returns {Promise} Adds main product + fee product to cart
	 */ 
	addToCartWithPersonalization(form_id) {
		const personalization = window.productPersonalizationInstance;
		
		if (this.checkoutButton) {
			this.checkoutButton.setAttribute("disabled", true);
			this.checkoutButtonTextEl.textContent = window.checkoutUpdatingButtonText;
		}
		
		const buttonText = document.getElementById(form_id)?.querySelector("[data-button-atc-text]");
		if (buttonText) {
			buttonText.disabled = true;
			buttonText.textContent = window.addToCartLoadingButtonText;
		}

		// Serialize main form
		const mainFormData = serialize(document.getElementById(form_id), { hash: true });
		
		// Add personalization inputs to main form properties
		const personalizationInputs = document.querySelectorAll(`product-personalization input[form="${form_id}"]`);
		if (personalizationInputs.length > 0) {
			mainFormData.properties = mainFormData.properties || {};
			personalizationInputs.forEach(input => {
				if (input.type === 'radio' || input.type === 'checkbox') {
					if (input.checked) {
						const propName = input.name.match(/properties\[(.*?)\]/)?.[1];
						if (propName) {
							mainFormData.properties[propName] = input.value;
						}
					}
				} else if (input.type === 'text' && input.value) {
					const propName = input.name.match(/properties\[(.*?)\]/)?.[1];
					if (propName) {
						mainFormData.properties[propName] = input.value;
					}
				}
			});
		}

		// Add line item properties from product__line-item-properties section
		mainFormData.properties = this.getLineItemProperties(form_id, mainFormData.properties || {});

		// Extract metadata from main form
		const { id: mainId, quantity: mainQty, properties: mainProps, ...baseFormData } = mainFormData;
		
		// Build items array - add main product first
		const items = [
			{
				id: mainId,
				quantity: mainQty || 1,
				properties: {
					...(mainProps || {}),
					'_LPROP': 'Final Sale'
				}
			}
		];

		// Add fee product as nested line item if personalization has a fee
		const feeVariantId = personalization.getFeeVariantId();
		if (feeVariantId) {
			items.push({
				id: feeVariantId,
				quantity: mainQty || 1,
				parent_id: mainId, // Link fee item to parent as nested line item
				properties: {
					...(mainProps || {}),
					'_Personalization - Product': personalization.getProductTitle(),
					'_LPROP': 'Final Sale'
				},
				instructions: {
					can_remove: false
				}
			});
		}

		const requestBody = {
			...baseFormData,
			items: items,
			sections: "cart-drawer-v2"
		};

		fetch(`${window.Shopify.routes.root}cart/add.js`, {
			body: JSON.stringify(requestBody),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest'
			},
			method: 'POST'
		})
		.then(response => response.json())
		.then((data) => {
			this.updateCartContents(data);

			if (data.status !== undefined && this.errorCodes.includes(data.status)) {
				alert(data.description);
				this.dispatchCartEvent('cart-error', data);
			} else {
				// KLAVIYO PRODUCT TRACKING for items
				if (data.items && Array.isArray(data.items)) {
					data.items.forEach(item => {
						var _learnq = window._learnq || [];
						var kl = {
							Name: `${item.product_title}`,
							ProductID: `${item.product_id}`,
							ImageURL: `${item.image}`,
							URL: `${window.location.origin}${item.url}`,
							Brand: `${item.vendor}`,
							Price: `${Shopify.formatMoney(item.final_price)}`,
							CompareAtPrice: `${Shopify.formatMoney(item.original_price || item.price)}`,
							VariantTitle: `${item.variant_title || ''}`,
							VariantPrice: `${Shopify.formatMoney(item.final_price)}`,
							VariantImage: `${item.image}`,
							VariantSKU: `${item.sku}`,
							VariantID: `${item.variant_id}`,
							VariantOption1: `${item.variant_options && item.variant_options[0] || ""}`,
							VariantOption2: `${item.variant_options && item.variant_options[1] || ""}`,
							VariantOption3: `${item.variant_options && item.variant_options[2] || ""}`
						};
						_learnq.push(['track', 'Added to Cart', kl]);
					});
				}

				this.updateBagCounts();
				
				// Dispatch cart-added event with form ID for addon processing
				this.dispatchCartEvent('cart-added', { ...data, formId: form_id });
				
				if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[name='add']") !== null){
					document.getElementById(form_id).querySelector("[name='add']").disabled = false;
					document.getElementById(form_id).querySelector("[name='add']").classList.remove("disabled");
					document.getElementById(form_id).querySelector("[name='add']").classList.remove("loading");
				}

				if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
					document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartAddedButtonText;
				}
				setTimeout(() => {		
					if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
						document.getElementById(form_id).querySelector("[data-button-atc-text]").disabled = false;
					}
					setTimeout(() => {
						this.open()
					}, 150);
					setTimeout(() => {				
						if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
							document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartButtonText;
						}
					}, 500);
				}, 300);
			}
		})
		.catch(error => {
			console.error('Error adding personalized product:', error);
			
			// Re-enable button on error
			if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[name='add']") !== null){
				document.getElementById(form_id).querySelector("[name='add']").disabled = false;
				document.getElementById(form_id).querySelector("[name='add']").classList.remove("disabled");
				document.getElementById(form_id).querySelector("[name='add']").classList.remove("loading");
			}

			if(document.getElementById(form_id) !== null && document.getElementById(form_id).querySelector("[data-button-atc-text]") !== null){
				document.getElementById(form_id).querySelector("[data-button-atc-text]").textContent = window.addToCartButtonText;
				document.getElementById(form_id).querySelector("[data-button-atc-text]").disabled = false;
			}
		});
	}

	/**
	 * ADD MULTIPLE FORM'S ITEMS TO THE CART // Submits multiple product forms via fetch() and adds the products to the slide-out cart
	 * @param  {Array} form_ids  Array of form IDs to submit
	 * @returns {(Promise|Function)} Returns the cart's json and then runs updateBagCounts() and showCart()
	 * @example window.addAllToCart(['add-to-cart', 'add-to-cart-2']); return false;
	 */ 
	addAllToCart(form_ids) {
		if (this.checkoutButton) {
			this.checkoutButton.setAttribute("disabled", true);
			this.checkoutButtonTextEl.textContent = window.checkoutUpdatingButtonText;
		}
	
		// Gather items from each form
		const items = form_ids.map(form_id => serialize(document.getElementById(form_id), { hash: true }));
	
		fetch(`${window.Shopify.routes.root}cart/add.js`, {
			body: JSON.stringify({ 
				...items,
				sections: "cart-drawer-v2"
			}),
			headers: {
				'Content-Type': 'application/json',
				'X-Requested-With': 'xmlhttprequest'
			},
			method: 'POST'
		})
		.then(response => response.json())
		.then((data) => {

			this.updateCartContents(data);

			if (data.status !== undefined && this.errorCodes.includes(data.status)) {
				alert(data.description);
			}
			// KLAVIYO PRODUCT TRACKING for each item
			data.items.forEach(item => {
				var _learnq = window._learnq || [];
				var kl = {
					Name: `${item.product_title}`,
					ProductID: `${item.id}`,
					ImageURL: `${item.image}`,
					URL: `${window.location.origin}${item.url}`,
					Brand: `${item.vendor}`,
					Price: `${Shopify.formatMoney(item.final_price)}`,
					CompareAtPrice: `${Shopify.formatMoney(item.original_price)}`,
					VariantTitle: `${item.variant_title}`,
					VariantPrice: `${Shopify.formatMoney(item.final_price)}`,
					VariantImage: `${item.image}`,
					VariantSKU: `${item.sku}`,
					VariantID: `${item.variant_id}`,
					VariantOption1: `${item.variant_options[0] || ""}`,
					VariantOption2: `${item.variant_options[1] || ""}`,
					VariantOption3: `${item.variant_options[2] || ""}`
				};
				_learnq.push(['track', 'Added to Cart', kl]);
			});
	
			this.updateBagCounts();
			this.open();
		});
	}	

	/**
	 * Updates a cart's item by line key and quantity via fetch().
	 * @param  {String} lineKey The line key of the item to update
	 * @param  {Number} qty The quantity to update to.
	 * @returns {Promise<void>} Updates the cart and refreshes the cart view.
	 */
	async cartItemUpdate(lineKey, qty) {
		if (this.checkoutButton) {
			this.checkoutButton.setAttribute("disabled", true);
			this.checkoutButtonTextEl.textContent = window.checkoutUpdatingButtonText;
		}

		try {
			// First, get current cart to find linked items
			const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then(r => r.json());
		
			// Find the item being updated by line key
			const updatedItem = cart.items.find(item => item.key === lineKey);

			if (!updatedItem) {
				console.error('Could not find item to update:', lineKey);
				throw new Error('Item not found in cart');
			}

			// Build updates object using line keys (not variant IDs)
			const updates = {};
			updates[lineKey] = qty;

			// Check if this is a parent item with nested fee items
			// Find nested items that have this item as their parent
			const nestedFeeItems = cart.items.filter(item => {
				if (item.key === lineKey) return false; // Skip the parent item itself
				
				// Check various possible parent relationship structures
				let isNested = false;
				
				if (item.parent_relationship) {					
          if (item.parent_relationship.parent_key === lineKey) {
						isNested = true;
					}
				}
				
				return isNested;
			});

			// Update all nested fee items to match parent quantity
			nestedFeeItems.forEach(nestedItem => {
				updates[nestedItem.key] = qty;
			});

			// Build form data with line keys
			const formData = new URLSearchParams();
			Object.keys(updates).forEach(key => {
				formData.append(`updates[${key}]`, updates[key]);
			});
			
			const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
				method: 'POST',
				headers: {
					'X-Requested-With': 'xmlhttprequest',
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: formData.toString() + '&sections=cart-drawer-v2',
			});

			if (!response.ok) {
				console.error(`Error: ${response.status} - ${response.statusText}`);
				throw new Error(`Failed to update cart: ${response.statusText}`);
			}

			const data = await response.json();
			this.updateCartContents(data);

			if (data.status !== undefined && this.errorCodes.includes(data.status)) {
				alert(data.description);
				this.dispatchCartEvent('cart-error', data);
			} else {
				this.announceCartItemUpdate(data.item_count);
				this.updateBagCounts();
				this.dispatchCartEvent('cart-updated', data);
				this.open();
			}
		} catch (error) {
			console.error('Cart update failed:', error);
			setTimeout(() => {
				this.updateBagCounts();
				this.open();
			}, 750);
		} finally {
			if (this.checkoutButton) {
				this.checkoutButton.removeAttribute("disabled");
				this.checkoutButtonTextEl.textContent = window.checkoutButtonText;
			}
		}
	}

   /**
    * ANNOUNCE CART ITEM UPDATE // Announces cart item update to screen readers
    */
   announceCartItemUpdate(qty) {

    // Create a visually hidden element to announce the update
    var updateAnnouncement = document.createElement('div');
    updateAnnouncement.textContent = 'Cart item quantity updated to ' + qty;
    updateAnnouncement.setAttribute('role', 'alert');
    updateAnnouncement.setAttribute('aria-live', 'polite');
    updateAnnouncement.setAttribute('aria-atomic', 'true');
    updateAnnouncement.style.position = 'absolute';
    updateAnnouncement.style.left = '-9999px';
    updateAnnouncement.style.width = '1px';
    updateAnnouncement.style.height = '1px';
    updateAnnouncement.style.overflow = 'hidden';
    
    // Append the announcement element to the document body
    document.body.appendChild(updateAnnouncement);
    
    // Remove the announcement element after a short delay
    setTimeout(function() {
      document.body.removeChild(updateAnnouncement);
    }, 1000); // Adjust the delay as needed

   }
}

// Register the custom element
customElements.define('cart-drawer', CartDrawerComponent);

// Global functions for backward compatibility
let cartDrawerInstance = null;

// Set up global functions immediately - they'll work once cart drawer is ready
window.updateBagCounts = (() => {
	if (cartDrawerInstance) {
		cartDrawerInstance.updateBagCounts();
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.updateBagCounts();
		});
	}
});

window.addToCart = ((form_id) => {
	if (cartDrawerInstance) {
		cartDrawerInstance.addToCart(form_id);
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.addToCart(form_id);
		});
	}
});

window.addAllToCart = ((form_ids) => {
	if (cartDrawerInstance) {
		cartDrawerInstance.addAllToCart(form_ids);
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.addAllToCart(form_ids);
		});
	}
});

window.cartItemUpdate = ((lineKey, qty) => {
	if (cartDrawerInstance) {
		cartDrawerInstance.cartItemUpdate(lineKey, qty);
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.cartItemUpdate(lineKey, qty);
		});
	}
});

window.showCart = (() => {
	if (cartDrawerInstance) {
		cartDrawerInstance.open();
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.open();
		});
	}
});

window.closeCart = (() => {
	if (cartDrawerInstance) {
		cartDrawerInstance.close();
	} else {
		waitForEl('cart-drawer').then(instance => {
			cartDrawerInstance = instance;
			cartDrawerInstance.close();
		});
	}
});

